// frontend/src/components/MembersModal.js

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "api";
import { CheckCircle, X } from "phosphor-react";
import { DEFAULT_PROFILE_IMAGE } from "../utils/blank-image";

const getMemberId = (member) =>
  member?.id ??
  member?.user_id ??
  member?.member_id ??
  member?.user?.id ??
  member?.member?.id ??
  null;

const normalizeMember = (member) => {
  const nestedUser = member?.user || member?.member || {};

  return {
    ...member,
    ...nestedUser,

    id:
      nestedUser?.id ??
      member?.id ??
      member?.user_id ??
      member?.member_id ??
      null,

    name:
      nestedUser?.name ??
      nestedUser?.display_name ??
      nestedUser?.username ??
      member?.name ??
      member?.display_name ??
      member?.username ??
      null,

    username:
      nestedUser?.username ??
      member?.username ??
      null,

    profile_pic_url:
      nestedUser?.profile_pic_url ??
      member?.profile_pic_url ??
      null,
  };
};

export default function MembersModal({
  open,
  onClose,
  members = [],
  meId,
  groupId,
}) {
  const [modalMembers, setModalMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState("");

  const [statusById, setStatusById] = useState({});
  const [busy, setBusy] = useState({});

  /*
   * Load full circle members when the modal opens.
   *
   * The group-members endpoint may only return partial member objects.
   * We then fetch /users/{id} so each row has profile_pic_url, name, etc.
   */
  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const loadMembers = async () => {
      setMembersLoading(true);
      setMembersError("");

      const token = localStorage.getItem("token");
      const headers = token
        ? { Authorization: `Bearer ${token}` }
        : undefined;

      try {
        let rawMembers = Array.isArray(members) ? members : [];

        /*
         * Prefer refreshing from the circle-members endpoint,
         * just as EngagementModal fetches its own current list.
         */
        if (groupId != null) {
          try {
            const response = await api.get(
              `/groups/${groupId}/members`,
              { headers }
            );

            if (Array.isArray(response?.data)) {
              rawMembers = response.data;
            }
          } catch {
            // Fall back to the members prop.
          }
        }

        const hydratedMembers = await Promise.all(
          rawMembers.map(async (rawMember) => {
            const normalized = normalizeMember(rawMember);
            const memberId = getMemberId(normalized);

            if (!memberId) {
              return normalized;
            }

            try {
              const response = await api.get(
                `/users/${memberId}`,
                { headers }
              );

              const fullUser = response?.data || {};

              return {
                ...normalized,
                ...fullUser,

                id:
                  fullUser?.id ??
                  normalized?.id ??
                  memberId,

                name:
                  fullUser?.name ??
                  fullUser?.display_name ??
                  fullUser?.username ??
                  normalized?.name ??
                  `User #${memberId}`,

                username:
                  fullUser?.username ??
                  normalized?.username ??
                  null,

                profile_pic_url:
                  fullUser?.profile_pic_url ??
                  fullUser?.avatar_url ??
                  fullUser?.profile_image_url ??
                  fullUser?.image_url ??
                  normalized?.profile_pic_url ??
                  null,
              };
            } catch {
              return normalized;
            }
          })
        );

        if (!cancelled) {
          setModalMembers(hydratedMembers);
        }
      } catch (error) {
        console.error(
          "Failed to load circle members:",
          error?.response?.data || error?.message || error
        );

        if (!cancelled) {
          setModalMembers(
            Array.isArray(members)
              ? members.map(normalizeMember)
              : []
          );

          setMembersError("Failed to refresh members.");
        }
      } finally {
        if (!cancelled) {
          setMembersLoading(false);
        }
      }
    };

    loadMembers();

    return () => {
      cancelled = true;
    };
  }, [open, groupId, members]);

  /*
   * Load connection status after the full member list is available.
   */
  useEffect(() => {
    if (!open || !modalMembers.length) return;

    let cancelled = false;

    const loadStatuses = async () => {
      const token = localStorage.getItem("token");
      const headers = token
        ? { Authorization: `Bearer ${token}` }
        : undefined;

      try {
        const subset = modalMembers
          .slice(0, 40)
          .map((member) => ({
            memberId: getMemberId(member),
          }))
          .filter(
            ({ memberId }) =>
              memberId != null &&
              String(memberId) !== String(meId)
          );

        const results = await Promise.all(
          subset.map(({ memberId }) =>
            api
              .get(`/connections/status/${memberId}`, {
                headers,
              })
              .then((response) => [
                memberId,
                response?.data?.status || "none",
              ])
              .catch(() => [memberId, "none"])
          )
        );

        if (cancelled) return;

        const nextStatuses = {};

        results.forEach(([memberId, status]) => {
          nextStatuses[memberId] = status;
        });

        setStatusById(nextStatuses);
      } catch {
        // Connection status is non-critical.
      }
    };

    loadStatuses();

    return () => {
      cancelled = true;
    };
  }, [open, modalMembers, meId]);

  const follow = async (otherId) => {
    if (!otherId) return;

    const token = localStorage.getItem("token");

    const headers = token
      ? {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        }
      : {
          "Content-Type": "application/json",
        };

    try {
      setBusy((current) => ({
        ...current,
        [otherId]: true,
      }));

      const { data } = await api.post(
        "/connections/request",
        {
          addressee_id: otherId,
        },
        {
          headers,
        }
      );

      const nextStatus =
        data?.status === "accepted"
          ? "accepted"
          : data?.status === "pending_out" ||
            data?.status === "requested" ||
            data?.status === "pending"
          ? "requested"
          : "none";

      setStatusById((current) => ({
        ...current,
        [otherId]: nextStatus,
      }));
    } catch (error) {
      console.error(
        error?.response?.data ||
          error?.message ||
          "Failed to follow member."
      );
    } finally {
      setBusy((current) => ({
        ...current,
        [otherId]: false,
      }));
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[75] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="members-modal-title"
      style={{
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        className="
          relative
          w-full max-w-[350px]
          rounded-2xl bg-white
          px-5 py-3
          shadow-[0_6px_0px_rgba(37,99,235,0.96)]
        "
        style={{ maxHeight: "90svh" }}
      >
        <div className="flex items-center justify-between">
          <h4
            id="members-modal-title"
            className="font-semibold text-blue-700"
          >
            Members
          </h4>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="!min-h-0 !h-7 !min-w-0 !py-0"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {membersError && (
          <div className="pt-2 text-xs text-red-500">
            {membersError}
          </div>
        )}

        {membersLoading ? (
          <div className="py-6 text-sm text-gray-600">
            Loading…
          </div>
        ) : modalMembers.length ? (
          <ul
            className="max-h-72 overflow-auto"
            style={{
              WebkitOverflowScrolling: "touch",
              paddingBottom:
                "env(safe-area-inset-bottom, 0px)",
            }}
          >
            {modalMembers.map((member, index) => {
              const memberId = getMemberId(member);

              const mine =
                memberId != null &&
                meId != null &&
                String(memberId) === String(meId);

              const status =
                statusById[memberId] || "none";

              const canFollow =
                !mine &&
                memberId != null &&
                status !== "accepted";

              const name =
                member?.name ||
                member?.display_name ||
                member?.username ||
                (memberId
                  ? `User #${memberId}`
                  : "User");

              const label =
                status === "accepted"
                  ? "Connected"
                  : status === "requested"
                  ? "Requested"
                  : "Follow";

              return (
                <li
                  key={
                    memberId ||
                    member?.username ||
                    `${name}-${index}`
                  }
                  className="flex items-center justify-between gap-3 py-2"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    {memberId ? (
                      <Link
                        to={`/profile/${memberId}`}
                        onClick={onClose}
                        className="flex min-w-0 items-center gap-3"
                      >
                        <img
                          src={
                            member?.profile_pic_url ||
                            DEFAULT_PROFILE_IMAGE
                          }
                          alt={name}
                          className="h-7 w-7 shrink-0 rounded-full object-cover"
                          loading="lazy"
                          decoding="async"
                        />

                        <span className="truncate text-sm font-medium text-gray-900 hover:font-bold hover:text-blue-600">
                          {name}
                        </span>
                      </Link>
                    ) : (
                      <>
                        <img
                          src={
                            member?.profile_pic_url ||
                            DEFAULT_PROFILE_IMAGE
                          }
                          alt={name}
                          className="h-7 w-7 shrink-0 rounded-full object-cover"
                          loading="lazy"
                          decoding="async"
                        />

                        <span className="truncate text-sm font-medium text-gray-900">
                          {name}
                        </span>
                      </>
                    )}
                  </div>

                  {canFollow ? (
                    <button
                      type="button"
                      onClick={() => follow(memberId)}
                      disabled={
                        busy[memberId] ||
                        status === "requested"
                      }
                      className={`
                        shrink-0 rounded-lg px-3 py-1
                        text-sm text-white transition
                        disabled:opacity-75
                        ${
                          status === "requested"
                            ? "cursor-default bg-blue-300"
                            : "bg-blue-500 hover:bg-blue-400"
                        }
                      `}
                    >
                      {busy[memberId] ? "…" : label}
                    </button>
                  ) : status === "accepted" ? (
                    <span className="flex shrink-0 items-center gap-1 px-3 text-xs text-blue-400">
                      <CheckCircle
                        size={14}
                        weight="bold"
                      />
                      Connected
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="py-6 text-sm text-gray-600">
            No members yet
          </div>
        )}
      </div>
    </div>
  );
}